import { buildAugusteLoginUrl } from "@/static/Auguste"
import { useEstablishmentStore } from "@/stores/establishment"

type AugusteSessionState = "idle" | "authenticating" | "authenticated" | "failed"

const AUTH_IFRAME_ID = "auguste-auth-iframe"
const AUTH_TIMEOUT_MS = 15000
const AUTH_SETTLE_MS = 1500

let state: AugusteSessionState = "idle"
let authenticationPromise: Promise<void> | null = null
let timeoutId: number | null = null
let authenticatedEstablishmentId: number | null = null

function getOrCreateAuthIframe(): HTMLIFrameElement {
  const existingIframe = document.getElementById(AUTH_IFRAME_ID)
  if (existingIframe instanceof HTMLIFrameElement) {
    return existingIframe
  }

  const iframe = document.createElement("iframe")
  iframe.id = AUTH_IFRAME_ID
  iframe.title = "Authentification Auguste"
  iframe.setAttribute("aria-hidden", "true")
  iframe.style.display = "none"
  iframe.style.width = "0"
  iframe.style.height = "0"
  iframe.style.border = "0"
  document.body.appendChild(iframe)

  return iframe
}

function clearAuthTimeout() {
  if (timeoutId !== null) {
    window.clearTimeout(timeoutId)
    timeoutId = null
  }
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

export function getAugusteSessionState(): AugusteSessionState {
  return state
}

export function resetAugusteSession() {
  clearAuthTimeout()
  authenticationPromise = null
  state = "idle"
  authenticatedEstablishmentId = null

  const iframe = document.getElementById(AUTH_IFRAME_ID)
  if (iframe) {
    iframe.remove()
  }
}

export function ensureAugusteAuthenticated(force = false): Promise<void> {
  const establishment = useEstablishmentStore().currentEstablishment
  const establishmentId = establishment?.id ?? null
  const bearerToken = establishment?.auguste_bearer_token

  if (
    state === "authenticated"
    && !force
    && authenticatedEstablishmentId === establishmentId
  ) {
    return Promise.resolve()
  }

  if (authenticationPromise && !force) {
    return authenticationPromise
  }

  if (!bearerToken) {
    state = "failed"
    authenticationPromise = null
    return Promise.reject(new Error("Token Auguste manquant pour cet établissement."))
  }

  state = "authenticating"
  const loginUrl = buildAugusteLoginUrl(bearerToken)

  authenticationPromise = new Promise((resolve, reject) => {
    const iframe = getOrCreateAuthIframe()

    const cleanup = () => {
      clearAuthTimeout()
      iframe.removeEventListener("load", handleLoad)
      iframe.removeEventListener("error", handleError)
    }

    const handleLoad = async () => {
      cleanup()
      // Laisse Auguste terminer la redirection /login -> /shootings et poser le cookie
      // avant de charger l'iframe visible du module demandé.
      await wait(AUTH_SETTLE_MS)
      state = "authenticated"
      authenticatedEstablishmentId = establishmentId
      resolve()
    }

    const handleError = () => {
      cleanup()
      state = "failed"
      authenticationPromise = null
      authenticatedEstablishmentId = null
      reject(new Error("Impossible d'authentifier la session Auguste."))
    }

    iframe.addEventListener("load", handleLoad, { once: true })
    iframe.addEventListener("error", handleError, { once: true })

    timeoutId = window.setTimeout(() => {
      cleanup()
      state = "failed"
      authenticationPromise = null
      authenticatedEstablishmentId = null
      reject(new Error("Le délai d'authentification Auguste est dépassé."))
    }, AUTH_TIMEOUT_MS)

    iframe.src = loginUrl
  })

  return authenticationPromise
}
